import * as React from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { API_URL, handleSendMessage } from "src/utilities";
import { useAuth } from "src/context/auth";
import { IService } from "src/interface/IService";
import { IServiceItem } from "src/interface/IServiceItem";
import { IFollowUpGroups } from "src/interface/IFollowUpGroups";
import { IAgreement } from "src/interface/IAgreement";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { useNotifications } from "@toolpad/core/useNotifications";
import SkeltonProgress from "src/components/SkeltonProgress";

export default function ListOfAgreementService({ rows }: { rows: IAgreement }) {
  const { user } = useAuth();

  // Mutation برای دریافت خدمات (Services)
  const [getServices, setServices] = React.useState<IAutoCompleteLabelValue[]>(
    []
  );
  const {
    mutate: fetchServices,
    isPending: isLoadingServices,
    error: servicesError,
  } = useMutation({
    mutationKey: ["ServicesData"],
    mutationFn: () =>
      fetch(API_URL + "services", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    onSuccess: (data: IService[]) => {
      setServices(
        data.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    },
    onError: (error) => {
      console.error("خطا در دریافت خدمات:", error);
    },
  });

  // Mutation برای دریافت گروه‌های پیگیری (FollowUpGroups)
  const [getFollowUps, setFollowUps] = React.useState<
    IAutoCompleteLabelValue[]
  >([]);

  const {
    mutate: fetchFollowUpGroups,
    isPending: isLoadingFollowUps,
    error: followUpsError,
  } = useMutation({
    mutationKey: ["FollowUpGroupsData"],
    mutationFn: () =>
      fetch(API_URL + "followup-groups", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    onSuccess: (data: IFollowUpGroups[]) => {
      setFollowUps(
        data.map((item) => ({
          label: item.title,
          value: item.id,
          item: item,
        }))
      );
    },
    onError: (error) => {
      console.error("خطا در دریافت گروه‌های پیگیری:", error);
    },
  });

  // Mutation برای دریافت آیتم‌های سرویس (ServiceItems)
  const [getServiceItem, setServiceItem] = React.useState<
    IAutoCompleteLabelValue[]
  >([]);
  const {
    mutate: fetchServiceItems,
    isPending: isLoadingServiceItems,
    error: serviceItemsError,
  } = useMutation({
    mutationKey: ["ServiceItemData"],
    mutationFn: () =>
      fetch(API_URL + "service-items", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
    onSuccess: (data: IServiceItem[]) => {
      setServiceItem(
        data.map((item) => ({
          label: item.title || "",
          value: item.id,
        }))
      );
    },
    onError: (error) => {
      console.error("خطا در دریافت آیتم‌های سرویس:", error);
    },
  });

  const notifications = useNotifications();
  React.useEffect(() => {
    if (servicesError) {
      handleSendMessage(
        notifications,
        "خطا در بارگیری" + servicesError.message,
        "error"
      );
    }
    if (followUpsError) {
      handleSendMessage(
        notifications,
        "خطا در بارگیری" + followUpsError.message,
        "error"
      );
    }
    if (serviceItemsError) {
      handleSendMessage(
        notifications,
        "خطا در بارگیری" + serviceItemsError.message,
        "error"
      );
    }
  }, [servicesError, followUpsError, serviceItemsError]);

  // اجرای اولیه درخواست‌ها (اختیاری)
  React.useEffect(() => {
    fetchServices();
    fetchFollowUpGroups();
    fetchServiceItems();
  }, [fetchServices, fetchFollowUpGroups, fetchServiceItems]);

  const columns: GridColDef[] = [
    {
      field: "serviceRef",
      headerName: "خدمت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      sortable: true,
      type: "singleSelect",
      valueOptions: getServices,
    },
    {
      field: "serviceItemByTypeRef",
      headerName: "نوع",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: getServiceItem,
    },
    {
      field: "serviceItemByVolumeRef",
      headerName: "حجم",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: getServiceItem,
    },
    {
      field: "count",
      headerName: "تعداد",
      minWidth: 100,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "followUpGroupRef",
      headerName: "گروه پیگیری",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",

      flex: 1,
      editable: true,

      sortable: true,
      type: "singleSelect",
      valueOptions: (params) =>
        getFollowUps.filter(
          (f) => f.item?.serviceRef === params.row?.serviceRef
        ),
    },

    {
      field: "isFollowUp",
      headerName: "پیگیری",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "boolean",
    },
    {
      field: "isPayment",
      headerName: "پرداخت",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      type: "boolean",
      sortable: true,
    },
    {
      field: "isApprovedByManager",
      headerName: "نیاز به تایید مدیر",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      type: "boolean",
      sortable: true,
    },
    {
      field: "desc",
      headerName: "توضیحات",
      minWidth: 200,
      filterable: true,
      align: "left",
      headerAlign: "left",
      flex: 2,
      editable: true,
      sortable: true,
      type: "string",
    },
  ];

  return (
    <Box>
      {isLoadingServices || isLoadingFollowUps || isLoadingServiceItems ? (
        <SkeltonProgress />
      ) : (
        <DataGrid
          //@ts-ignore
          rows={rows.services}
          columns={columns}
          editMode="row"
          //rowModesModel={rowModesModel}
          //onRowModesModelChange={handleRowModesModelChange}
          //onRowEditStop={handleRowEditStop}
          //processRowUpdate={processRowUpdate}
          //@ts-ignore

          // slotProps={{
          //   //@ts-ignore
          //   toolbar: {
          //     setRows,
          //     //setRowModesModel,
          //     rows,
          //   },
          // }}
        />
      )}
    </Box>
  );
}
